/**
 * ShowcaseFrame — rounded "app window" chrome around a live product surface,
 * used by the Coaching OS marketing showcase components so each surface reads
 * as "inside the product". Prop-only and edge-safe: imports nothing from
 * src/lib/server, src/lib/crypto, src/lib/queries, or src/routes.
 *
 * - variant "hero": fills its container (h-full w-full) so it sits inside the
 *   ProductShowcase fixed-aspect media panel; its content scales rather than
 *   reflows on narrow widths.
 * - variant "feature": fills its slot up to a `max-w-2xl` marketing-window cap,
 *   so it reads as a contained product window inside the FeatureShowcase
 *   two-column slot while still using the room a wide placement gives it.
 *
 * The frame is a query container (`@container`): the surfaces inside respond to
 * the *frame's* width, not the viewport, so the same component lays out
 * correctly whether it's dropped into a narrow Builder slot, a half-width
 * feature column, or a wide hero panel. Inner surfaces express their narrow
 * layouts as `@max-*` overrides on a wide base, which keeps the shared product
 * components they reuse visually unchanged in the real app (no `@container`
 * ancestor there ⇒ the overrides never fire).
 *
 * Aliases: showcase frame, app window chrome, product window, browser frame.
 */

import { type ReactNode } from "react";

import { cn } from "~/lib/cn";

type ShowcaseFrameVariant = "hero" | "feature";

export function ShowcaseFrame({
  variant = "feature",
  className,
  children,
}: {
  readonly variant?: ShowcaseFrameVariant;
  readonly className?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div
      role="presentation"
      className={cn(
        "@container flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-900/5",
        variant === "feature" ? "w-full max-w-2xl" : "h-full w-full",
        className,
      )}
    >
      {/* Decorative window chrome — skipped by assistive tech. */}
      <div
        aria-hidden="true"
        className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3"
      >
        <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
      </div>
      <div
        className={cn(
          "min-h-0",
          variant === "hero" && "flex-1 overflow-hidden",
        )}
      >
        {children}
      </div>
    </div>
  );
}
